import { selectRouterPathname } from '@noths/polaris-client-next-redux';
import type { AnyAction, Middleware, ThunkDispatch } from '@reduxjs/toolkit';
import { LOCATION_CHANGE } from 'connected-next-router';
import type { LocationChangeAction } from 'connected-next-router/actions';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { isValidShopTaxonomyPathPrefix } from 'src/services/contentstack/taxonomyNeighborhood/getParentPathWithoutFilters';
import { syncTaxonomyContentFromURL } from '../thunks/syncTaxonomyContentFromURL';

type ReduxThunkDispatch = ThunkDispatch<ReduxApplicationState, undefined, AnyAction>;

const isLocationChangeAction = (action: AnyAction): action is LocationChangeAction =>
  action.type === LOCATION_CHANGE;

/*
    connected-next-router dispatches LOCATION_CHANGE for every client-side
    navigation. For shop taxonomy PLPs we keep the editorial content correct by
    syncing the neighbourhood map + selected entry's content from the new URL.
    The previous pathname is read before next(action) because LOCATION_CHANGE
    updates the router slice. Initial content is seeded server-side, so this
    middleware only handles client navigations.
*/
export const taxonomyContentURLSyncMiddleware: Middleware<
  unknown,
  ReduxApplicationState,
  ReduxThunkDispatch
> =
  ({ dispatch, getState }) =>
  (next) =>
  (action) => {
    if (typeof window === 'undefined' || !isLocationChangeAction(action)) {
      return next(action);
    }

    const previousPath = selectRouterPathname(getState());

    const result = next(action);

    const newPath = action.payload.location.pathname;

    if (!isValidShopTaxonomyPathPrefix(newPath)) {
      return result;
    }

    (dispatch as ReduxThunkDispatch)(syncTaxonomyContentFromURL({ previousPath, newPath }));

    return result;
  };
